import { Permission } from '@bookora/shared';

import { joinFa } from './text';
import type { TourDef } from './types';

/**
 * «کارکنان و نقش‌ها» (`/staff`). A row press opens the colleague's drawer,
 * whose tabs are each behind their own key (`staff-drawer.tsx`: نقش و دسترسی
 * ← staff.manage_roles, برنامهٔ کاری ← settings.edit to change, سهم درآمد ←
 * finance.manage_revenue_share). The invite dialog asks for a mobile number
 * and a role; «دعوت‌ها» is drawn only for someone who can invite or when the
 * clinic has colleagues, and its resend actions are the inviter's.
 */
export const STAFF_TOUR: TourDef = {
  key: 'staff',
  version: 1,
  surface: 'page',
  title: 'کارکنان و نقش‌ها',
  hubDescription: 'دعوت کارمند، دعوت‌های فرستاده‌شده، نقش‌ها و دسترسی‌ها.',
  offer: {
    body: 'در این راهنما می‌بینید چطور همکار جدید دعوت کنید و سطح دسترسی هر نقش رو مشخص کنید.',
  },
  route: { path: '/staff', match: (pathname) => pathname === '/staff' },
  requires: { all: [Permission.STAFF_VIEW] },
  finish: {
    label: 'دعوت کارمند',
    // The page opens the invite dialog on `?invite=1` and drops the flag.
    to: '/staff?invite=1',
    requires: { all: [Permission.STAFF_INVITE] },
  },
  autoOffer: true,
  maxSteps: 4,
  minSteps: 2,
  steps: [
    {
      id: 'team',
      anchor: 'staff.team',
      placement: 'top',
      title: 'مشخصات هر کارمند',
      body: (ctx) => {
        const settable = [
          ctx.can(Permission.STAFF_MANAGE_ROLES) ? 'نقش' : null,
          ctx.can(Permission.SETTINGS_EDIT) ? 'برنامهٔ کاری' : null,
          ctx.can(Permission.FINANCE_MANAGE_REVENUE_SHARE) ? 'سهم درآمد' : null,
        ].filter((x): x is string => x !== null);
        const open = 'روی هر کارمند بزنید تا مشخصاتش باز بشه.';
        return settable.length > 0
          ? `${open} ${joinFa(settable)} هر کس هم همون‌جا تنظیم می‌شه.`
          : open;
      },
    },
    {
      id: 'invite',
      anchor: 'staff.invite',
      placement: 'bottom-end',
      requires: { all: [Permission.STAFF_INVITE] },
      title: 'دعوت کارمند',
      body: 'شماره موبایل کارمند رو بنویسید و نقشش رو انتخاب کنید. پیامکی براش می‌ره و با همون شماره وارد پنل می‌شه.',
    },
    {
      id: 'invites',
      anchor: 'staff.tab-invites',
      placement: 'bottom',
      title: 'دعوت‌های فرستاده‌شده',
      body: (ctx) =>
        ctx.can(Permission.STAFF_INVITE)
          ? 'دعوتی که هنوز پذیرفته نشده اینجا می‌مونه. اگر پیامکش نرسید یا مهلتش گذشت، از همین‌جا دوباره بفرستید.'
          : 'دعوتی که هنوز پذیرفته نشده اینجا می‌مونه.',
    },
    {
      id: 'roles',
      anchor: 'staff.tab-roles',
      placement: 'bottom',
      title: 'نقش‌ها و دسترسی‌ها',
      body: (ctx) =>
        ctx.can(Permission.STAFF_MANAGE_ROLES)
          ? 'هر نقش مشخص می‌کنه کارمند چه اطلاعاتی رو ببینه و چه کارهایی بتونه انجام بده. نقش جدید هم همین‌جا ساخته می‌شه.'
          : 'هر نقش مشخص می‌کنه کارمند چه اطلاعاتی رو ببینه و چه کارهایی بتونه انجام بده.',
    },
  ],
};
